/**
 * The audit-log table: one summary row per entry, newest first, with an
 * expandable sibling row carrying that entry's documents.
 *
 * Every cell is rendered from the entry alone (BR-F1.5-12). The role is the
 * stored CODE, verbatim, in one neutral chip: the label lives in a table the
 * read path may not join, and a frontend map would be wrong for exactly the
 * historical rows whose role has since been renamed (design D7). The entity is
 * its type plus a short id prefix. No business name is resolved, because the
 * row it names may not exist any more.
 *
 * Outcome is the one mapped value, and safely so: it is a closed set of three
 * enforced by a database CHECK.
 */

import { ChevronDown, Filter } from 'lucide-react';

import type { AuditLogEntrySummary, AuditOutcome } from '@/api/client';
import { EmptyCell } from '@/ui/EmptyCell';
import { EMPTY_CELL } from '@/ui/emptyValue';
import { SortableHeader } from '@/ui/SortableHeader';
import type { SortState } from '@/ui/tableSort';

import { AuditEntryDetail } from './AuditEntryDetail';
import { actorFilterFor, type ActorFilter } from './auditFilters';
import type { SortColumn } from './auditSort';
import { formatTimestamp, type TimeZoneMode } from './auditTime';
import { NON_REQUEST_SENTINEL, shortId } from './payloadView';

const OUTCOME_PILL: Record<AuditOutcome, { className: string; label: string }> = {
  SUCCESS: { className: 'pill-success', label: 'Success' },
  DENIED: { className: 'pill-warning', label: 'Denied' },
  FAILED: { className: 'pill-danger', label: 'Failed' },
};

const COLUMN_COUNT = 10;

interface RowProps {
  entry: AuditLogEntrySummary;
  /** The clock this row's timestamp renders in, chosen in the filter bar. */
  timeZone: TimeZoneMode;
  expanded: boolean;
  onToggle: () => void;
  onFilterActor: (actor: ActorFilter) => void;
}

function AuditRow({ entry, timeZone, expanded, onToggle, onFilterActor }: RowProps) {
  const detailId = `audit-detail-${entry.id}`;
  const outcome = OUTCOME_PILL[entry.outcome];
  const isJob = entry.http_method === NON_REQUEST_SENTINEL;
  const verified = entry.actor_user_id !== null;

  return (
    <>
      <tr
        className={`audit-row outcome-${entry.outcome.toLowerCase()}${expanded ? ' is-expanded' : ''}`}
      >
        <td className="audit-toggle-cell">
          <button
            type="button"
            className="icon-btn audit-toggle"
            aria-expanded={expanded}
            aria-controls={detailId}
            aria-label={expanded ? 'Hide details' : 'Show details'}
            onClick={onToggle}
          >
            <ChevronDown aria-hidden="true" />
          </button>
        </td>
        <td className="mono audit-ts" title={entry.occurred_at}>
          {formatTimestamp(entry.occurred_at, timeZone)}
        </td>
        <td>
          <button
            type="button"
            className={`actor-filter${verified ? '' : ' is-unverified'}`}
            title={`Add ${entry.actor_display} to the actor filter`}
            onClick={() => onFilterActor(actorFilterFor(entry))}
          >
            <span className="actor-name">{entry.actor_display}</span>
            <Filter aria-hidden="true" />
          </button>
        </td>
        <td>
          {entry.actor_role_at_time ? (
            <span className="badge pill-neutral role-code">{entry.actor_role_at_time}</span>
          ) : (
            <EmptyCell />
          )}
        </td>
        <td>
          <span className="badge badge-chip">{entry.action}</span>
        </td>
        {/* The entity type and its id are two facts and two columns. Packed
            into one cell they could be neither read nor sorted apart, and
            the type is what a reader scans while the id is what they copy. */}
        <td>{entry.entity_type ? <span className="audit-entity">{entry.entity_type}</span> : <EmptyCell />}</td>
        <td className="mono hint" title={entry.entity_id ?? undefined}>
          {entry.entity_id ? shortId(entry.entity_id) : <EmptyCell />}
        </td>
        <td>
          <span className={`badge ${outcome.className}`}>{outcome.label}</span>
        </td>
        <td className={isJob || !entry.source_ip ? 'mono hint' : 'mono'}>
          {isJob ? 'background job' : (entry.source_ip ?? EMPTY_CELL)}
        </td>
        <td className="mono hint" title={entry.request_id ?? undefined}>
          {entry.request_id ? shortId(entry.request_id) : EMPTY_CELL}
        </td>
      </tr>
      {expanded && (
        <tr id={detailId} className="audit-detail-row">
          <td />
          <td colSpan={COLUMN_COUNT - 1}>
            <AuditEntryDetail entryId={entry.id} timeZone={timeZone} />
          </td>
        </tr>
      )}
    </>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }, (_, index) => (
        <tr key={index} className="skeleton-row" aria-hidden="true">
          <td />
          {Array.from({ length: COLUMN_COUNT - 1 }, (__, cell) => (
            <td key={cell}>
              <span className="skeleton-bar" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

interface TableProps {
  entries: AuditLogEntrySummary[];
  /** The clock every timestamp renders in, and the name printed beside them. */
  timeZone: TimeZoneMode;
  zone: string;
  loading: boolean;
  expandedId: string | null;
  sort: SortState<SortColumn>;
  onSort: (column: SortColumn) => void;
  onToggle: (entryId: string) => void;
  onFilterActor: (actor: ActorFilter) => void;
}

export function AuditLogTable({
  entries,
  timeZone,
  zone,
  loading,
  expandedId,
  sort,
  onSort,
  onToggle,
  onFilterActor,
}: TableProps) {
  return (
    <div className="table-scroll">
      <table className="data compact audit-table" aria-busy={loading}>
        <thead>
          {/* Every column sorts except the first, which holds the expand
              control. The order is over the entries LOADED, which the note
              under the table says out loud. */}
          <tr>
            <th className="audit-toggle-cell" aria-label="Details" />
            <SortableHeader column="timestamp" label="Timestamp" sort={sort} onSort={onSort}>
              <span className="zone-label">({zone})</span>
            </SortableHeader>
            <SortableHeader column="actor" label="Actor" sort={sort} onSort={onSort} />
            <SortableHeader column="role" label="Role at time" sort={sort} onSort={onSort} />
            <SortableHeader column="action" label="Action" sort={sort} onSort={onSort} />
            <SortableHeader column="entityType" label="Entity" sort={sort} onSort={onSort} />
            <SortableHeader column="entityId" label="Entity id" sort={sort} onSort={onSort} />
            <SortableHeader column="outcome" label="Outcome" sort={sort} onSort={onSort} />
            <SortableHeader column="source" label="Source" sort={sort} onSort={onSort} />
            <SortableHeader column="request" label="Request" sort={sort} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows />
          ) : (
            entries.map((entry) => (
              <AuditRow
                key={entry.id}
                entry={entry}
                timeZone={timeZone}
                expanded={expandedId === entry.id}
                onToggle={() => onToggle(entry.id)}
                onFilterActor={onFilterActor}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
